import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  BookOpen, Search, Plus, Clock, Users, IndianRupee, Star,
  RefreshCw, GraduationCap, Award, TrendingUp, Eye, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CenterCoursesPage = () => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      // Fetch courses from Admin API - Single source of truth
      const response = await axios.get(`${API}/courses`, { headers: getAuthHeaders() });
      const adminCourses = response.data || [];
      
      // Map admin courses to display format with enrolled count from center_students
      const mappedCourses = adminCourses.map(course => ({
        id: course.id,
        name: course.name,
        duration: course.duration || '3 Months',
        fee: course.fee || 0,
        yearlyFee: course.yearly_fee || course.fee || 0,
        totalFee: course.total_fee || course.fee || 0,
        category: course.category || 'Professional',
        enrolled: course.enrolled_count || 0,
        rating: course.rating || 4.5,
        share: course.category?.includes('Degree') || course.category === 'Diploma' ? '40:60' : '70:30',
        years: course.duration_years || 1,
        modules: course.modules || [],
        description: course.description || ''
      }));
      
      setCourses(mappedCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', 'Professional', 'Distance', 'Diploma', 'Degree', 'Vocational'];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name?.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesCategory = categoryFilter === 'all';
    if (!matchesCategory) {
      const courseCat = (course.category || '').toLowerCase();
      const filterCat = categoryFilter.toLowerCase();
      matchesCategory = courseCat === filterCat || 
                        courseCat.includes(filterCat) || 
                        (filterCat === 'degree' && (courseCat.includes('degree') || courseCat.includes('ug') || courseCat.includes('pg')));
    }
    return matchesSearch && matchesCategory;
  });

  const totalEnrolled = courses.reduce((sum, c) => sum + (c.enrolled || 0), 0);

  const getCategoryColor = (category) => {
    const cat = (category || '').toLowerCase();
    if (cat === 'professional') return 'bg-emerald-100 text-emerald-700';
    if (cat === 'distance') return 'bg-cyan-100 text-cyan-700';
    if (cat.includes('ug') || cat === 'degree') return 'bg-blue-100 text-blue-700';
    if (cat.includes('pg')) return 'bg-purple-100 text-purple-700';
    if (cat === 'diploma') return 'bg-amber-100 text-amber-700';
    if (cat === 'vocational') return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-courses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Courses</h1>
          <p className="text-slate-500 mt-1">{courses.length} courses available at your center</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchCourses} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-emerald-200" />
            <div>
              <p className="text-emerald-100 text-sm">Professional</p>
              <p className="text-2xl font-bold">{courses.filter(c => c.category === 'Professional').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-blue-200" />
            <div>
              <p className="text-blue-100 text-sm">Degree Programs</p>
              <p className="text-2xl font-bold">{courses.filter(c => c.category?.includes('Degree')).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Award className="w-8 h-8 text-amber-200" />
            <div>
              <p className="text-amber-100 text-sm">Diploma</p>
              <p className="text-2xl font-bold">{courses.filter(c => c.category === 'Diploma').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-200" />
            <div>
              <p className="text-purple-100 text-sm">Total Enrolled</p>
              <p className="text-2xl font-bold">{totalEnrolled}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                categoryFilter === cat 
                  ? 'bg-purple-600 text-white shadow-lg' 
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Course Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCourses.map((course) => (
          <motion.div
            key={course.id}
            whileHover={{ y: -5 }}
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all"
          >
            <div className={`h-3 ${
              course.category === 'Professional' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
              course.category?.includes('UG') ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
              course.category?.includes('PG') ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
              'bg-gradient-to-r from-amber-500 to-amber-600'
            }`} />
            <div className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg">{course.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(course.category)}`}>
                    {course.category}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-amber-500">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm font-medium">{course.rating}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-slate-500 mt-3">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {course.duration}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {course.enrolled} enrolled
                </div>
              </div>
              
              {/* Fee Structure */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                {course.yearlyFee ? (
                  // Degree/Diploma Course
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Per Year Fee</span>
                      <span className="text-lg font-bold text-emerald-600">₹{course.yearlyFee.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Total ({course.years} years)</span>
                      <span className="text-slate-500">₹{course.totalFee.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Revenue Share</span>
                      <span className="text-blue-600 font-medium">Center {course.share?.split(':')[0]}% | IQRA {course.share?.split(':')[1]}%</span>
                    </div>
                  </div>
                ) : (
                  // Professional Course
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Course Fee</span>
                      <span className="text-lg font-bold text-emerald-600">₹{course.fee?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Revenue Share</span>
                      <span className="text-emerald-600 font-medium">Center {course.share?.split(':')[0]}% | IQRA {course.share?.split(':')[1]}%</span>
                    </div>
                  </div>
                )}
              </div>
              
              <Button variant="outline" size="sm" className="w-full mt-4 gap-1">
                <Eye className="w-4 h-4" />
                View Details
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500">No courses found</p>
        </div>
      )}
    </div>
  );
};

export default CenterCoursesPage;
